import { Chip, FormControl, FormHelperText, OutlinedInput, Stack, Typography, useMediaQuery } from '@mui/material'
import React, { FunctionComponent, ReactElement, SetStateAction } from 'react'
import gaiaTheme from '../../../../root-components/theme'
import { TooltipButton } from '../../miscellaneous-components/tooltip-button'
import FormEditButtons from './form-edit-buttons'

export const FormTextInputDisplay: FunctionComponent<{
  isEditPage: boolean,
  allowEdit?: boolean,
  editField?: string | undefined,
  setEditField?: React.Dispatch<SetStateAction<any>> | undefined,
  hideEditIcon?: boolean,
  disabled: boolean,
  isLastField?: boolean,
  title: string,
  name: string,
  type?: 'text' | 'email' | 'number' | 'date'
  value: string | number | undefined,
  touched?: any,
  errors?: any,
  onChange: any,
  cancelEditHandler?: () => void,
  onEditClick?: () => void,
  endAdornment?: ReactElement,
  startAdornment?: ReactElement,
  customErrorMessage?: string,
  tooltip?: string,
  bottomBorder?: boolean,
  topBorder?: boolean,
  sideBorders?: boolean,
  isTopRow?: boolean,
  isBottomRow?: boolean,
  includePlaceholder?: boolean,
  placeholder?: string,
  forceOutlined?: boolean,
  chip?: string,
  chipColor?: 'default' | 'primary' | 'secondary' | 'success',
  multiline?: boolean,
  rows?: number,
  regularFieldLabel?: boolean,
  helperText?: string,
}> = ({
  isEditPage,
  allowEdit = true,
  editField,
  setEditField,
  hideEditIcon = false,
  disabled,
  isLastField = false,
  title,
  name,
  type = 'text',
  value,
  touched,
  errors,
  onChange,
  cancelEditHandler,
  onEditClick,
  endAdornment,
  startAdornment,
  customErrorMessage,
  tooltip,
  bottomBorder = false,
  topBorder = true,
  sideBorders = true,
  isTopRow = false,
  isBottomRow = false,
  includePlaceholder = false,
  placeholder,
  forceOutlined = false,
  chip,
  chipColor = 'primary',
  multiline = false,
  rows,
  regularFieldLabel = false,
  helperText,
}) => {

    const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));

    const toggleEditField = (fieldName: string) => {
      if (setEditField) {
        setEditField(fieldName)
      }
    }

    return (
      <FormControl fullWidth variant='outlined'>
        <Stack sx={{
          borderWidth: '1px',
          borderBottomStyle: bottomBorder || isBottomRow ? 'solid' : 'none',
          borderColor: 'lightGrey.main',
          borderLeftStyle: sideBorders ? 'solid' : 'none',
          borderRightStyle: sideBorders ? 'solid' : 'none',
          borderTopStyle: topBorder || isTopRow ? 'solid' : 'none',
          pt: 1, pb: 1, pl: 3,
          borderTopLeftRadius: isTopRow ? '10px' : 0,
          borderTopRightRadius: isTopRow ? '10px' : 0,
          borderBottomLeftRadius: isBottomRow ? '10px' : 0,
          borderBottomRightRadius: isBottomRow ? '10px' : 0
        }}>
          <Stack position='relative' alignItems={isMobileMode ? 'flex-start' : 'center'} flexDirection={isMobileMode ? 'column' : 'row'} justifyContent='start'>
            <Stack sx={{ width: isMobileMode ? '100%' : '40%', minWidth: '100px', color: 'text.primary', display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <Typography variant='subtitle1' sx={regularFieldLabel ? { fontWeight: 400 } : undefined}>{title}</Typography>
              {tooltip && !isMobileMode && <TooltipButton
                text={tooltip}
                noPadding={true}
                placement='bottom' />}
            </Stack>
            <Stack position='relative' flexDirection='column' sx={{ width: '100%' }}>
              <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'>
                {chip && value === '' ? (
                  <Chip label={chip} variant='outlined' color={chipColor} sx={{ ml: 1, mt: 1, mb: 1 }} />
                ) : (
                  <>
                    <OutlinedInput id={name} size='small' name={name} type={type}
                      value={value}
                      disabled={disabled}
                      onChange={onChange}
                      fullWidth
                      multiline={multiline}
                      rows={rows}
                      placeholder={placeholder ?? (includePlaceholder ? title : undefined)}
                      startAdornment={startAdornment ? startAdornment : null}
                      endAdornment={endAdornment ? endAdornment : null}
                      inputProps={type === 'number'
                        ? (name === 'totalRevenue'
                          ? { min: 0, step: 1 }
                          : { min: 0, step: 0.0001 })
                        : undefined}
                      error={touched && Boolean(errors)}
                      sx={{
                        mt: 1, mb: 1, mr: 15, ml: 1,
                        ...((!forceOutlined && (editField !== name || isEditPage)) && {
                          '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
                        }),
                        ...(((forceOutlined) || editField === name || !isEditPage) && {
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'text.secondary' }
                        }),
                        ...(!isEditPage && {
                          mt: 1, mb: 1, mr: 2, ml: 2,
                        }),
                        color: 'text.primary'
                      }}></OutlinedInput>
                    {chip && <Chip label={chip} variant='outlined' color={chipColor} sx={{ mr: 2 }} />}
                  </>
                )}
                {allowEdit &&
                  <FormEditButtons
                    isEditPage={isEditPage}
                    editField={editField}
                    hideEditIcon={hideEditIcon}
                    name={name}
                    toggleEditField={toggleEditField}
                    cancelEditHandler={cancelEditHandler}
                    onEditClick={onEditClick}
                  />}
              </Stack>
              {touched && errors &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {errors ? <div>{errors as string}</div> : null}
                </FormHelperText>
              }
              {customErrorMessage &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{customErrorMessage}
                </FormHelperText>
              }
              {helperText && !(touched && errors) && !customErrorMessage &&
                <FormHelperText sx={{ color: "text.secondary", marginLeft: "16px !important" }}>{helperText}
                </FormHelperText>
              }
            </Stack>
          </Stack>
        </Stack>
      </FormControl>
    )
  }